import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { groupService } from '../../services/groupService';
import { GroupStatusBadge } from '../../components/common/StatusBadge';
import { formatCurrency } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';

function JoinModal({ onClose }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');

  const mutation = useMutation({
    mutationFn: () => groupService.joinByCode({ invite_code: code.trim().toUpperCase() }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['my-groups'] });
      navigate(`/groups/${res.data.data.id}`);
    },
    onError: (e) => setErr(e.response?.data?.message || 'Could not join group.'),
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: 380 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 700 }}>Join a Group</h2>
          <button className="btn btn-ghost" style={{ padding: '0.25rem 0.5rem' }} onClick={onClose}>✕</button>
        </div>
        <div className="form-group">
          <label className="form-label">Invite Code</label>
          <input className="form-input" value={code} onChange={e => setCode(e.target.value)} placeholder="e.g. SHD9KFER" style={{ textTransform: 'uppercase' }} />
        </div>
        {err && <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}><span>⚠</span><span>{err}</span></div>}
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button className="btn btn-primary" style={{ flex: 1 }} disabled={mutation.isPending || !code.trim()} onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Joining…' : 'Join'}
          </button>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function Groups() {
  const navigate = useNavigate();
  const [showJoin, setShowJoin] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['my-groups'],
    queryFn: () => groupService.getMyGroups().then(r => r.data.data),
  });

  const groups = data?.results || [];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>My Groups</h1>
          <p>Group contribution circles (Ajo) you organize or belong to</p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button className="btn btn-ghost" onClick={() => setShowJoin(true)}>Join with Code</button>
          <Link to="/groups/new" className="btn btn-primary">New Group</Link>
        </div>
      </div>

      {isLoading ? <LoadingSpinner /> : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Group</th>
                  <th>Contribution</th>
                  <th>Cycle</th>
                  <th>Members</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {groups.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-3)', padding: '2rem' }}>
                      No groups yet. <Link to="/groups/new" style={{ color: 'var(--primary)' }}>Start one →</Link>
                    </td>
                  </tr>
                ) : groups.map(group => (
                  <tr key={group.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/groups/${group.id}`)}>
                    <td style={{ fontWeight: 600 }}>{group.name}</td>
                    <td>{formatCurrency(group.contribution_amount)}</td>
                    <td style={{ textTransform: 'capitalize', fontSize: '0.875rem' }}>{group.cycle}</td>
                    <td>{group.member_count}/{group.max_members}</td>
                    <td><GroupStatusBadge status={group.status} /></td>
                    <td onClick={e => e.stopPropagation()}>
                      <Link to={`/groups/${group.id}`} className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '0.3rem 0.75rem' }}>View</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showJoin && <JoinModal onClose={() => setShowJoin(false)} />}
    </div>
  );
}
